import React, { useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v1';

// ─── helpers ───────────────────────────────────────────────────────────────

async function adminFetch(path: string, token: string, opts: RequestInit = {}) {
  const res = await fetch(`${API_BASE}/admin${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

function StatCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color: string }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-5 flex flex-col gap-1`}>
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</span>
      <span className={`text-3xl font-bold ${color}`}>{value}</span>
      {sub && <span className="text-xs text-gray-400">{sub}</span>}
    </div>
  );
}

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {label}
    </span>
  );
}

function Pagination({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center gap-2 mt-4 justify-end">
      <button
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="px-3 py-1 rounded border border-gray-200 text-sm disabled:opacity-40 hover:bg-gray-50"
      >
        Prev
      </button>
      <span className="text-sm text-gray-600">
        {page} / {totalPages}
      </span>
      <button
        disabled={page >= totalPages}
        onClick={() => onChange(page + 1)}
        className="px-3 py-1 rounded border border-gray-200 text-sm disabled:opacity-40 hover:bg-gray-50"
      >
        Next
      </button>
    </div>
  );
}

// ─── Login screen ───────────────────────────────────────────────────────────

function AdminLogin({ onLogin }: { onLogin: (token: string, admin: any) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const data = await fetch(`${API_BASE}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      }).then((r) => {
        if (!r.ok) return r.json().then((d) => Promise.reject(d.error || 'Login failed'));
        return r.json();
      });
      localStorage.setItem('piezit-admin-token', data.token);
      onLogin(data.token, data.admin);
    } catch (err: any) {
      setError(err.toString());
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Piezit Admin</h1>
          <p className="text-gray-500 text-sm mt-1">Sign in to access the admin portal</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              placeholder="admin@piezit.com"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-60 transition-colors"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ─── Overview Tab ───────────────────────────────────────────────────────────

function OverviewTab({ token }: { token: string }) {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [purgePassword, setPurgePassword] = useState('');
  const [purgeConfirmPhrase, setPurgeConfirmPhrase] = useState('');
  const [purging, setPurging] = useState(false);
  const [purgeError, setPurgeError] = useState('');
  const [purgeSuccess, setPurgeSuccess] = useState('');

  const loadStats = useCallback(() => {
    setLoading(true);
    setError('');
    adminFetch('/stats', token)
      .then(setStats)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const resetPurgeModal = () => {
    setShowPurgeModal(false);
    setPurgePassword('');
    setPurgeConfirmPhrase('');
    setPurgeError('');
  };

  const handlePurge = async () => {
    setPurging(true);
    setPurgeError('');
    try {
      const result = await adminFetch('/data/purge', token, {
        method: 'POST',
        body: JSON.stringify({
          password: purgePassword,
          confirmPhrase: purgeConfirmPhrase,
        }),
      });
      const totalDeleted = Object.values(result.deleted || {}).reduce(
        (sum: number, count) => sum + Number(count),
        0
      );
      setPurgeSuccess(
        `Platform data reset complete. Removed ${totalDeleted.toLocaleString()} records. Admin account(s) preserved: ${(result.preservedAdminEmails || []).join(', ')}`
      );
      resetPurgeModal();
      loadStats();
    } catch (err: any) {
      setPurgeError(err.message || 'Failed to purge platform data');
    } finally {
      setPurging(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading statistics…</div>;
  if (error) return <div className="text-red-600 p-4">{error}</div>;
  if (!stats) return null;

  const { overview, revenueByPlan, reviewTrend, topCategories } = stats;

  return (
    <div className="space-y-8">
      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Users" value={overview.totalUsers.toLocaleString()} sub={`+${overview.newUsersThisMonth} this month`} color="text-indigo-600" />
        <StatCard label="Total Businesses" value={overview.totalBusinesses.toLocaleString()} color="text-emerald-600" />
        <StatCard label="Total Reviews" value={overview.totalReviews.toLocaleString()} sub={`+${overview.newReviewsThisMonth} this month`} color="text-amber-600" />
        <StatCard label="Monthly Revenue" value={`$${overview.monthlyRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`} sub={`${overview.activeBusinessMembers} active companies`} color="text-rose-600" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Business Members" value={overview.totalBusinessMembers.toLocaleString()} color="text-purple-600" />
        <StatCard label="Locations" value={overview.totalLocations.toLocaleString()} color="text-cyan-600" />
        <StatCard label="Verified Users" value={overview.verifiedUsers.toLocaleString()} color="text-teal-600" />
        <StatCard label="Flagged Reviews" value={overview.flaggedReviews.toLocaleString()} color="text-red-600" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Revenue by plan */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Revenue by Plan</h3>
          <div className="space-y-3">
            {revenueByPlan.map((p: any) => (
              <div key={p.planName} className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-700">{p.planName}</span>
                  <span className="ml-2 text-xs text-gray-400">{p.count} companies</span>
                </div>
                <span className="text-sm font-semibold text-emerald-600">
                  ${parseFloat(p.revenue).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            ))}
            {revenueByPlan.length === 0 && <p className="text-gray-400 text-sm">No revenue data yet</p>}
          </div>
        </div>

        {/* Top categories */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-800 mb-4">Top Business Categories</h3>
          <div className="space-y-2">
            {topCategories.map((c: any) => (
              <div key={c.category} className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-700">{c.category}</span>
                    <span className="text-gray-500">{c.count}</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 rounded-full">
                    <div
                      className="h-1.5 bg-indigo-500 rounded-full"
                      style={{ width: `${Math.min(100, (c.count / (topCategories[0]?.count || 1)) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>
            ))}
            {topCategories.length === 0 && <p className="text-gray-400 text-sm">No category data yet</p>}
          </div>
        </div>
      </div>

      {/* Review trend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <h3 className="font-semibold text-gray-800 mb-4">Reviews (Last 30 Days)</h3>
        {reviewTrend.length > 0 ? (
          <div className="flex items-end gap-1 h-24">
            {reviewTrend.map((d: any) => {
              const max = Math.max(...reviewTrend.map((x: any) => x.count));
              const pct = max > 0 ? (d.count / max) * 100 : 0;
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                  <div
                    className="w-full bg-indigo-400 rounded-t"
                    style={{ height: `${Math.max(pct, 4)}%` }}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-400 text-sm">No review data in the last 30 days</p>
        )}
      </div>

      {/* Danger zone */}
      <div className="bg-white rounded-xl shadow-sm border border-red-200 p-5">
        <h3 className="font-semibold text-red-700 mb-2">Danger Zone</h3>
        <p className="text-sm text-gray-600 mb-4">
          Permanently delete all users, businesses, reviews, companies, and related data.
          Admin accounts and membership plans are preserved so you can start fresh.
        </p>
        {purgeSuccess && (
          <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-3 text-sm">
            {purgeSuccess}
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            setPurgeSuccess('');
            setShowPurgeModal(true);
          }}
          className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors"
        >
          Reset platform data
        </button>
      </div>

      {showPurgeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Reset platform data?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This action cannot be undone. All user accounts, businesses, reviews, and companies
              will be permanently deleted. Admin accounts will be kept.
            </p>
            {purgeError && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
                {purgeError}
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="font-mono text-red-600">PURGE ALL DATA</span> to confirm
                </label>
                <input
                  type="text"
                  value={purgeConfirmPhrase}
                  onChange={(e) => setPurgeConfirmPhrase(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="PURGE ALL DATA"
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Your admin password
                </label>
                <input
                  type="password"
                  value={purgePassword}
                  onChange={(e) => setPurgePassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  autoComplete="current-password"
                />
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                type="button"
                onClick={resetPurgeModal}
                disabled={purging}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePurge}
                disabled={
                  purging ||
                  purgeConfirmPhrase !== 'PURGE ALL DATA' ||
                  !purgePassword
                }
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {purging ? 'Resetting…' : 'Permanently reset data'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Users Tab ───────────────────────────────────────────────────────────────

function UsersTab({ token }: { token: string }) {
  const [data, setData] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    adminFetch(`/users?page=${page}&limit=20&search=${encodeURIComponent(debouncedSearch)}`, token)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, page, debouncedSearch]);

  const toggleAdmin = async (userId: string, current: boolean) => {
    try {
      await adminFetch(`/users/${userId}`, token, {
        method: 'PATCH',
        body: JSON.stringify({ is_admin: !current }),
      });
      setData((prev: any) => ({
        ...prev,
        data: prev.data.map((u: any) => u.id === userId ? { ...u, is_admin: !current } : u),
      }));
    } catch (e: any) {
      alert(e.message);
    }
  };

  return (
    <div>
      <div className="mb-4 flex gap-3">
        <input
          type="text"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : error ? (
        <div className="text-red-600">{error}</div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left">Name / Email</th>
                  <th className="px-4 py-3 text-left">Joined</th>
                  <th className="px-4 py-3 text-center">Reviews</th>
                  <th className="px-4 py-3 text-center">Verified</th>
                  <th className="px-4 py-3 text-center">Admin</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data?.data.map((u: any) => (
                  <tr key={u.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{u.name}</div>
                      <div className="text-gray-400 text-xs">{u.email}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-500">{new Date(u.joined_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{u.total_reviews}</td>
                    <td className="px-4 py-3 text-center">
                      {u.verified ? <Badge label="Yes" color="bg-emerald-100 text-emerald-700" /> : <Badge label="No" color="bg-gray-100 text-gray-500" />}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {u.is_admin ? <Badge label="Admin" color="bg-indigo-100 text-indigo-700" /> : <Badge label="User" color="bg-gray-100 text-gray-500" />}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleAdmin(u.id, u.is_admin)}
                        className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-600"
                      >
                        {u.is_admin ? 'Revoke Admin' : 'Make Admin'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data?.data.length === 0 && (
              <div className="text-center py-8 text-gray-400">No users found</div>
            )}
          </div>
          <Pagination page={page} totalPages={data?.pagination?.totalPages || 1} onChange={setPage} />
        </>
      )}
    </div>
  );
}

// ─── Businesses Tab ──────────────────────────────────────────────────────────

function BusinessesTab({ token }: { token: string }) {
  const [data, setData] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(() => {
    setLoading(true);
    adminFetch(`/businesses?page=${page}&limit=20&search=${encodeURIComponent(debouncedSearch)}`, token)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, page, debouncedSearch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleActive = async (id: string, current: boolean) => {
    try {
      await adminFetch(`/businesses/${id}`, token, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !current }),
      });
      fetchData();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const toggleVerified = async (id: string, current: boolean) => {
    try {
      await adminFetch(`/businesses/${id}`, token, {
        method: 'PATCH',
        body: JSON.stringify({ is_verified: !current }),
      });
      fetchData();
    } catch (e: any) {
      alert(e.message);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by name or address…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : error ? (
        <div className="text-red-600">{error}</div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left">Name / Address</th>
                  <th className="px-4 py-3 text-left">Category</th>
                  <th className="px-4 py-3 text-center">Rating</th>
                  <th className="px-4 py-3 text-center">Reviews</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data?.data.map((b: any) => (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{b.name}</div>
                      <div className="text-gray-400 text-xs truncate max-w-xs">{b.address}</div>
                      {b.member_email && <div className="text-indigo-400 text-xs">{b.member_email}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-500">{b.category}</td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-medium text-amber-600">
                        {parseFloat(b.average_rating || 0).toFixed(1)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">{b.total_reviews}</td>
                    <td className="px-4 py-3 text-center space-x-1">
                      {b.is_active
                        ? <Badge label="Active" color="bg-emerald-100 text-emerald-700" />
                        : <Badge label="Inactive" color="bg-red-100 text-red-600" />}
                      {b.is_verified && <Badge label="Verified" color="bg-indigo-100 text-indigo-700" />}
                    </td>
                    <td className="px-4 py-3 text-center space-x-1">
                      <button
                        onClick={() => toggleActive(b.id, b.is_active)}
                        className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-600"
                      >
                        {b.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => toggleVerified(b.id, b.is_verified)}
                        className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-600"
                      >
                        {b.is_verified ? 'Unverify' : 'Verify'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data?.data.length === 0 && (
              <div className="text-center py-8 text-gray-400">No businesses found</div>
            )}
          </div>
          <Pagination page={page} totalPages={data?.pagination?.totalPages || 1} onChange={setPage} />
        </>
      )}
    </div>
  );
}

// ─── Reviews Tab ─────────────────────────────────────────────────────────────

function ReviewsTab({ token }: { token: string }) {
  const [data, setData] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reassignReview, setReassignReview] = useState<any>(null);
  const [bizSearch, setBizSearch] = useState('');
  const [bizList, setBizList] = useState<any[]>([]);
  const [reassigning, setReassigning] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({
      page: String(page),
      limit: '20',
      search: debouncedSearch,
      ...(flaggedOnly ? { is_flagged: 'true' } : {}),
    });
    adminFetch(`/reviews?${params}`, token)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, page, debouncedSearch, flaggedOnly]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!bizSearch) { setBizList([]); return; }
    const t = setTimeout(() => {
      adminFetch(`/businesses/list?search=${encodeURIComponent(bizSearch)}`, token)
        .then((d) => setBizList(d.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [bizSearch, token]);

  const handleFlag = async (id: string, current: boolean) => {
    try {
      await adminFetch(`/reviews/${id}`, token, {
        method: 'PATCH',
        body: JSON.stringify({ is_flagged: !current }),
      });
      fetchData();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleToggleActive = async (id: string, current: boolean) => {
    try {
      await adminFetch(`/reviews/${id}`, token, {
        method: 'PATCH',
        body: JSON.stringify({ is_active: !current }),
      });
      fetchData();
    } catch (e: any) {
      alert(e.message);
    }
  };

  const handleReassign = async (businessId: string) => {
    if (!reassignReview) return;
    setReassigning(true);
    try {
      await adminFetch(`/reviews/${reassignReview.id}/reassign`, token, {
        method: 'PATCH',
        body: JSON.stringify({ business_id: businessId }),
      });
      setReassignReview(null);
      setBizSearch('');
      setBizList([]);
      fetchData();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setReassigning(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex gap-3 flex-wrap">
        <input
          type="text"
          placeholder="Search reviews, businesses, users…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="flex-1 min-w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
        />
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={flaggedOnly}
            onChange={(e) => { setFlaggedOnly(e.target.checked); setPage(1); }}
            className="rounded"
          />
          Flagged only
        </label>
      </div>

      {/* Reassign modal */}
      {reassignReview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-1">Reassign Review</h3>
            <p className="text-sm text-gray-500 mb-4">
              "{reassignReview.title}" — currently on <strong>{reassignReview.business_name}</strong>
            </p>
            <input
              type="text"
              placeholder="Search for target business…"
              value={bizSearch}
              onChange={(e) => setBizSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3 focus:ring-2 focus:ring-indigo-500"
            />
            {bizList.length > 0 && (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-56 overflow-y-auto mb-3">
                {bizList.map((b: any) => (
                  <button
                    key={b.id}
                    onClick={() => handleReassign(b.id)}
                    disabled={reassigning || b.id === reassignReview.business_id}
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50 disabled:opacity-40 text-sm"
                  >
                    <div className="font-medium text-gray-800">{b.name}</div>
                    <div className="text-xs text-gray-400">{b.address} · {b.category}</div>
                  </button>
                ))}
              </div>
            )}
            {bizSearch && bizList.length === 0 && (
              <p className="text-gray-400 text-sm mb-3">No businesses found</p>
            )}
            <div className="flex justify-end">
              <button
                onClick={() => { setReassignReview(null); setBizSearch(''); setBizList([]); }}
                className="px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : error ? (
        <div className="text-red-600">{error}</div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left">Review</th>
                  <th className="px-4 py-3 text-left">Business</th>
                  <th className="px-4 py-3 text-left">User</th>
                  <th className="px-4 py-3 text-center">Rating</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data?.data.map((r: any) => (
                  <tr key={r.id} className={`hover:bg-gray-50 ${r.is_flagged ? 'bg-red-50/40' : ''}`}>
                    <td className="px-4 py-3 max-w-xs">
                      <div className="font-medium text-gray-800 truncate">{r.title}</div>
                      <div className="text-gray-400 text-xs truncate">{r.content}</div>
                      <div className="text-gray-300 text-xs">{new Date(r.created_at).toLocaleDateString()}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-700 truncate max-w-[140px]">{r.business_name}</div>
                      <div className="text-gray-400 text-xs">{r.business_category}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-gray-700">{r.user_name}</div>
                      <div className="text-gray-400 text-xs">{r.user_email}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-medium text-amber-600">{'★'.repeat(r.rating)}</span>
                    </td>
                    <td className="px-4 py-3 text-center space-x-1">
                      {r.is_flagged && <Badge label="Flagged" color="bg-red-100 text-red-600" />}
                      {!r.is_active && <Badge label="Hidden" color="bg-gray-100 text-gray-500" />}
                      {r.is_active && !r.is_flagged && <Badge label="Active" color="bg-emerald-100 text-emerald-700" />}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex gap-1 justify-center flex-wrap">
                        <button
                          onClick={() => setReassignReview(r)}
                          className="text-xs px-2 py-1 rounded border border-indigo-200 hover:bg-indigo-50 text-indigo-600"
                        >
                          Reassign
                        </button>
                        <button
                          onClick={() => handleFlag(r.id, r.is_flagged)}
                          className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-600"
                        >
                          {r.is_flagged ? 'Unflag' : 'Flag'}
                        </button>
                        <button
                          onClick={() => handleToggleActive(r.id, r.is_active)}
                          className="text-xs px-2 py-1 rounded border border-gray-200 hover:bg-gray-50 text-gray-600"
                        >
                          {r.is_active ? 'Hide' : 'Show'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data?.data.length === 0 && (
              <div className="text-center py-8 text-gray-400">No reviews found</div>
            )}
          </div>
          <Pagination page={page} totalPages={data?.pagination?.totalPages || 1} onChange={setPage} />
        </>
      )}
    </div>
  );
}

// ─── Companies Tab ───────────────────────────────────────────────────────────

function CompaniesTab({ token }: { token: string }) {
  const [data, setData] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setLoading(true);
    adminFetch(`/business-members?page=${page}&limit=20&search=${encodeURIComponent(debouncedSearch)}`, token)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, page, debouncedSearch]);

  const totalRevenue = data?.data.reduce((sum: number, m: any) => sum + parseFloat(m.total_monthly_fee || 0), 0) || 0;

  return (
    <div>
      <div className="mb-4 flex gap-3 items-center">
        <input
          type="text"
          placeholder="Search companies…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="flex-1 max-w-sm px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
        />
        {data && (
          <div className="text-sm text-gray-500 ml-auto">
            Page revenue: <strong className="text-emerald-600">${totalRevenue.toFixed(2)}</strong>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : error ? (
        <div className="text-red-600">{error}</div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left">Company</th>
                  <th className="px-4 py-3 text-left">Plan</th>
                  <th className="px-4 py-3 text-center">Locations</th>
                  <th className="px-4 py-3 text-center">Reviews</th>
                  <th className="px-4 py-3 text-center">Avg Rating</th>
                  <th className="px-4 py-3 text-right">Monthly Fee</th>
                  <th className="px-4 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data?.data.map((m: any) => (
                  <tr key={m.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{m.business_name}</div>
                      <div className="text-gray-400 text-xs">{m.email}</div>
                      <div className="text-gray-400 text-xs">{m.owner_name}</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge
                        label={m.plan_name || 'Unknown'}
                        color={
                          m.plan_name === 'Enterprise' ? 'bg-purple-100 text-purple-700' :
                          m.plan_name === 'Professional' ? 'bg-indigo-100 text-indigo-700' :
                          'bg-gray-100 text-gray-600'
                        }
                      />
                    </td>
                    <td className="px-4 py-3 text-center text-gray-700">{m.location_count}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{m.total_reviews || 0}</td>
                    <td className="px-4 py-3 text-center text-amber-600 font-medium">
                      {parseFloat(m.average_rating || 0).toFixed(1)}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-emerald-600">
                      ${parseFloat(m.total_monthly_fee || 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {m.is_active
                        ? <Badge label="Active" color="bg-emerald-100 text-emerald-700" />
                        : <Badge label="Inactive" color="bg-red-100 text-red-600" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data?.data.length === 0 && (
              <div className="text-center py-8 text-gray-400">No companies found</div>
            )}
          </div>
          <Pagination page={page} totalPages={data?.pagination?.totalPages || 1} onChange={setPage} />
        </>
      )}
    </div>
  );
}

// ─── Locations Tab ───────────────────────────────────────────────────────────

function LocationsTab({ token }: { token: string }) {
  const [data, setData] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newLoc, setNewLoc] = useState({ org_id: '', name: '', address: '', latitude: '40.7128', longitude: '-74.0060', phone: '' });
  const [members, setMembers] = useState<any[]>([]);
  const [linkTarget, setLinkTarget] = useState<any>(null);
  const [bizSearch, setBizSearch] = useState('');
  const [bizList, setBizList] = useState<any[]>([]);
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = useCallback(() => {
    setLoading(true);
    adminFetch(`/locations?page=${page}&limit=20&search=${encodeURIComponent(debouncedSearch)}`, token)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, page, debouncedSearch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Load business members for "Create Location" dropdown
  useEffect(() => {
    adminFetch('/business-members?limit=100', token)
      .then((d) => setMembers(d.data || []))
      .catch(() => {});
  }, [token]);

  // Load businesses for link dropdown
  useEffect(() => {
    if (!bizSearch) { setBizList([]); return; }
    const t = setTimeout(() => {
      adminFetch(`/businesses/list?search=${encodeURIComponent(bizSearch)}`, token)
        .then((d) => setBizList(d.data))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [bizSearch, token]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      await adminFetch('/locations', token, {
        method: 'POST',
        body: JSON.stringify({
          ...newLoc,
          latitude: parseFloat(newLoc.latitude),
          longitude: parseFloat(newLoc.longitude),
        }),
      });
      setShowCreate(false);
      setNewLoc({ org_id: '', name: '', address: '', latitude: '40.7128', longitude: '-74.0060', phone: '' });
      fetchData();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setCreating(false);
    }
  };

  const handleLink = async (businessId: string) => {
    if (!linkTarget) return;
    setLinking(true);
    try {
      await adminFetch(`/locations/${linkTarget.id}/link`, token, {
        method: 'POST',
        body: JSON.stringify({ business_id: businessId }),
      });
      setLinkTarget(null);
      setBizSearch('');
      setBizList([]);
      fetchData();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setLinking(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex gap-3 flex-wrap items-center">
        <input
          type="text"
          placeholder="Search locations…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="flex-1 min-w-48 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
        />
        <button
          onClick={() => setShowCreate(!showCreate)}
          className="text-sm px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          + Create Location
        </button>
      </div>

      {/* Create Location Form */}
      {showCreate && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-4">
          <h3 className="font-semibold text-gray-800 mb-4">Create Location for Company</h3>
          <form onSubmit={handleCreate} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Company *</label>
              <select required value={newLoc.org_id} onChange={e => setNewLoc(p => ({...p, org_id: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option value="">Select company…</option>
                {members.map((m: any) => (
                  <option key={m.id} value={m.id}>{m.business_name} ({m.email})</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Location Name *</label>
                <input required type="text" value={newLoc.name} onChange={e => setNewLoc(p => ({...p, name: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Downtown Branch" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Phone</label>
                <input type="tel" value={newLoc.phone} onChange={e => setNewLoc(p => ({...p, phone: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="(212) 555-0100" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Address *</label>
              <input required type="text" value={newLoc.address} onChange={e => setNewLoc(p => ({...p, address: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="123 Main Street, New York, NY 10001" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Latitude *</label>
                <input required type="number" step="any" value={newLoc.latitude} onChange={e => setNewLoc(p => ({...p, latitude: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Longitude *</label>
                <input required type="number" step="any" value={newLoc.longitude} onChange={e => setNewLoc(p => ({...p, longitude: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
            </div>
            <div className="flex gap-2">
              <button type="submit" disabled={creating}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60">
                {creating ? 'Creating…' : 'Create Location'}
              </button>
              <button type="button" onClick={() => setShowCreate(false)}
                className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-50">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Link modal */}
      {linkTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
            <h3 className="font-semibold text-gray-900 mb-1">Link Location to Business Listing</h3>
            <p className="text-sm text-gray-500 mb-4">
              Location: <strong>{linkTarget.name}</strong> — search for the public map pin to link it to.
            </p>
            <input
              type="text"
              placeholder="Search businesses…"
              value={bizSearch}
              onChange={(e) => setBizSearch(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3"
            />
            {bizList.length > 0 && (
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto mb-3">
                {bizList.map((b: any) => (
                  <button key={b.id} disabled={linking} onClick={() => handleLink(b.id)}
                    className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm disabled:opacity-50">
                    <div className="font-medium text-gray-800">{b.name}</div>
                    <div className="text-xs text-gray-400">{b.address} · {b.category}</div>
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => { setLinkTarget(null); setBizSearch(''); setBizList([]); }}
              className="w-full px-4 py-2 border border-gray-200 text-sm text-gray-600 rounded-lg hover:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : error ? (
        <div className="text-red-600">{error}</div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                <tr>
                  <th className="px-4 py-3 text-left">Location Name / Address</th>
                  <th className="px-4 py-3 text-left">Company</th>
                  <th className="px-4 py-3 text-left">Linked Business</th>
                  <th className="px-4 py-3 text-center">Rating</th>
                  <th className="px-4 py-3 text-center">Status</th>
                  <th className="px-4 py-3 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data?.data.map((l: any) => (
                  <tr key={l.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800">{l.name}</div>
                      <div className="text-gray-400 text-xs truncate max-w-xs">{l.address}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">{l.member_name}</td>
                    <td className="px-4 py-3">
                      {l.linked_business_name ? (
                        <>
                          <div className="text-gray-700">{l.linked_business_name}</div>
                          <div className="text-gray-400 text-xs">{l.total_reviews} reviews</div>
                        </>
                      ) : (
                        <Badge label="Unlinked" color="bg-yellow-100 text-yellow-700" />
                      )}
                    </td>
                    <td className="px-4 py-3 text-center text-amber-600 font-medium">
                      {l.average_rating ? parseFloat(l.average_rating).toFixed(1) : '—'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {l.is_active
                        ? <Badge label="Active" color="bg-emerald-100 text-emerald-700" />
                        : <Badge label="Inactive" color="bg-red-100 text-red-600" />}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => setLinkTarget(l)}
                        className="text-xs px-2 py-1 rounded border border-indigo-200 hover:bg-indigo-50 text-indigo-600"
                      >
                        {l.linked_business_name ? 'Relink' : 'Link'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data?.data.length === 0 && (
              <div className="text-center py-8 text-gray-400">No locations found</div>
            )}
          </div>
          <Pagination page={page} totalPages={data?.pagination?.totalPages || 1} onChange={setPage} />
        </>
      )}
    </div>
  );
}

// ─── Duplicates Tab ──────────────────────────────────────────────────────────

function DuplicatesTab({ token }: { token: string }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [merging, setMerging] = useState(false);

  const fetchData = useCallback(() => {
    setLoading(true);
    adminFetch('/duplicates', token)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMerge = async (keepId: string, mergeId: string, keepName: string, mergeName: string) => {
    if (!window.confirm(`Merge "${mergeName}" INTO "${keepName}"?\n\nThis will reassign all reviews and deactivate "${mergeName}". This cannot be undone.`)) return;
    setMerging(true);
    try {
      const result = await adminFetch('/businesses/merge', token, {
        method: 'POST',
        body: JSON.stringify({ keep_id: keepId, merge_id: mergeId }),
      });
      alert(`Merged successfully. ${result.conflictsDeactivated} conflicting review(s) were deactivated.`);
      fetchData();
    } catch (e: any) {
      alert(e.message);
    } finally {
      setMerging(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Potential duplicate businesses detected by matching name or proximity (&lt;200m, same category).
        </p>
        <button
          onClick={fetchData}
          className="text-sm px-3 py-1.5 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-600"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Scanning for duplicates…</div>
      ) : error ? (
        <div className="text-red-600">{error}</div>
      ) : (
        <>
          {data?.data.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
              <svg className="w-12 h-12 text-emerald-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="font-medium text-gray-800">No duplicates detected</h3>
              <p className="text-sm text-gray-400 mt-1">All businesses appear to be unique</p>
            </div>
          ) : (
            <div className="space-y-4">
              {data.data.map((pair: any, idx: number) => (
                <div key={idx} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                  <div className="flex items-center gap-2 mb-4">
                    <Badge
                      label={pair.reason === 'same_name' ? 'Same Name' : 'Nearby & Same Category'}
                      color={pair.reason === 'same_name' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}
                    />
                    {pair.distance_m && (
                      <span className="text-xs text-gray-400">{Math.round(pair.distance_m)}m apart</span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { id: pair.id1, name: pair.name1, address: pair.address1, category: pair.category1, reviews: pair.reviews1, rating: pair.rating1 },
                      { id: pair.id2, name: pair.name2, address: pair.address2, category: pair.category2, reviews: pair.reviews2, rating: pair.rating2 },
                    ].map((biz, i) => (
                      <div key={i} className="border border-gray-100 rounded-lg p-3 bg-gray-50/50">
                        <div className="font-medium text-gray-800">{biz.name}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{biz.address}</div>
                        <div className="text-xs text-gray-500 mt-1">{biz.category}</div>
                        <div className="flex gap-3 mt-2 text-xs text-gray-500">
                          <span className="text-amber-600 font-medium">★ {parseFloat(biz.rating || 0).toFixed(1)}</span>
                          <span>{biz.reviews} reviews</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex gap-2">
                    <button
                      disabled={merging}
                      onClick={() => handleMerge(pair.id1, pair.id2, pair.name1, pair.name2)}
                      className="flex-1 text-sm py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
                    >
                      Keep "{pair.name1}", merge other in
                    </button>
                    <button
                      disabled={merging}
                      onClick={() => handleMerge(pair.id2, pair.id1, pair.name2, pair.name1)}
                      className="flex-1 text-sm py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 font-medium"
                    >
                      Keep "{pair.name2}", merge other in
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main Admin Portal ────────────────────────────────────────────────────────

type Tab = 'overview' | 'businesses' | 'reviews' | 'users' | 'companies' | 'locations' | 'duplicates';

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: 'overview', label: 'Overview', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
  { id: 'businesses', label: 'Businesses', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4' },
  { id: 'reviews', label: 'Reviews', icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z' },
  { id: 'users', label: 'Users', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
  { id: 'companies', label: 'Companies', icon: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
  { id: 'locations', label: 'Locations', icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z' },
  { id: 'duplicates', label: 'Duplicates', icon: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z' },
];

const AdminPortalPage: React.FC = () => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('piezit-admin-token'));
  const [admin, setAdmin] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  // Validate existing token on mount
  useEffect(() => {
    if (token) {
      adminFetch('/stats', token)
        .then(() => {/* valid */})
        .catch(() => {
          localStorage.removeItem('piezit-admin-token');
          setToken(null);
        });
    }
  // eslint-disable-next-line
  }, []);

  if (!token) {
    return (
      <AdminLogin
        onLogin={(t, a) => {
          setToken(t);
          setAdmin(a);
        }}
      />
    );
  }

  const handleLogout = () => {
    localStorage.removeItem('piezit-admin-token');
    setToken(null);
    setAdmin(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className="w-56 bg-gray-900 flex flex-col fixed top-0 left-0 h-full z-30">
        <div className="p-5 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <div className="text-white font-semibold text-sm">Piezit Admin</div>
              {admin && <div className="text-gray-400 text-xs truncate max-w-[96px]">{admin.email}</div>}
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                activeTab === tab.id
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
              </svg>
              {tab.label}
            </button>
          ))}
        </nav>

        <div className="p-3 border-t border-gray-700">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Sign out
          </button>
          <a
            href="/"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:bg-gray-800 hover:text-white transition-colors mt-1"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            Back to App
          </a>
        </div>
      </div>

      {/* Main content */}
      <div className="ml-56 flex-1 p-6 min-w-0">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {TABS.find((t) => t.id === activeTab)?.label}
          </h2>
        </div>

        {activeTab === 'overview' && <OverviewTab token={token} />}
        {activeTab === 'businesses' && <BusinessesTab token={token} />}
        {activeTab === 'reviews' && <ReviewsTab token={token} />}
        {activeTab === 'users' && <UsersTab token={token} />}
        {activeTab === 'companies' && <CompaniesTab token={token} />}
        {activeTab === 'locations' && <LocationsTab token={token} />}
        {activeTab === 'duplicates' && <DuplicatesTab token={token} />}
      </div>
    </div>
  );
};

export default AdminPortalPage;
